/**
 * English catalogue for namespace 'session' (W1-16 app-integration): what the services wiring and the project
 * session tell the user (src/app/services.ts, src/app/projectSession.ts, src/app/viewStatePersist.ts): song and
 * hitsound loading, crash recovery, another tab taking the chart over, and optional features that are not available
 * in this browser. Imports only src/i18n/define.ts (catalogue zone, ARCHITECTURE.md §4).
 */

import { catalogOf, defineMessages } from '../i18n/define';

export const m = defineMessages('session', {
  /** The opened chart's song could not be read or decoded (the chart still opens, without sound). */
  songLoadFailed: 'The song could not be loaded: {reason}. The chart is open without music.',
  /** The song's bytes are gone from the browser's storage (W3-01, UX-016). */
  songMissing: 'The song file "{file}" is missing from this browser\'s storage, so the chart is open without music.',
  /** An imported package referenced a song file it did not contain. */
  songNotInPackage: '"{file}" was not in the package, so the chart is open without music.',
  /** Toast action: choose the song file again (Assets: replace). */
  addSong: 'Add the song',
  /** Why a chart could not be opened, by storage failure (W3-01, UX-016; no ids or storage jargon). */
  openFailedReason:
    "{code, select, lockedElsewhere {This chart is open in another tab. Only one tab can edit a chart at a time: switch to that tab, or open the chart here from the Library to move it.} lockLost {Another tab took this chart over.} notFound {This chart is no longer in this browser's storage. It may have been deleted in another tab.} quota {The browser's storage is full, so the chart could not be loaded. Free some space (Settings, Storage) and try again.} io {The browser's storage could not be read. Try again; if it keeps failing, reload the page.} other {Something went wrong while loading it: {error}}}",
  /** Custom hitsound files of the chart could not be decoded. */
  hitsoundsFailed:
    '{count, plural, one {# custom hitsound} other {# custom hitsounds}} could not be loaded. Those notes play the skin sound instead.',
  /** Opening found changes of a session that ended without closing the chart (crash, killed tab). */
  recovered: 'Recovered {count, plural, one {# edit} other {# edits}} from the last session.',
  /** Replaying the journal stopped early: the chart opened at its last good state. */
  replayErrorTitle: 'Some recent changes could not be restored',
  replayErrorBody:
    'The chart opened at its last good state. Replaying the saved changes stopped with: {error}. Download the recovery data to keep the rest (for a bug report or manual recovery).',
  replayErrorDownload: 'Download recovery data',
  replayErrorContinue: 'Continue editing',
  /** The recovery data could not be read for download. */
  recoveryFailed: 'The recovery data could not be read: {error}',
  /** The saved undo history could not be loaded; editing works, older steps are in Versions. */
  historyNotRestored: 'The undo history could not be restored. Earlier states are still in Versions.',
  /** Another tab took the chart over while this one was editing it. */
  lockLostTitle: 'This chart is now edited in another tab',
  lockLostBody:
    'Another tab took over "{name}", so this tab can no longer save it. Changes made here after the takeover were not saved. Reload to continue with the latest version.',
  lockLostReload: 'Reload',
  /**
   * A link, a reload or "Try again" asked for a chart another tab is editing (the Library's own flow handles its
   * opens). Two choices (DESIGN.md §5.13).
   */
  lockedTitle: '"{name}" is open in another tab',
  lockedBody: 'Only one tab can edit a chart at a time, so changes from two tabs never mix.',
  lockedSwitch: 'Switch to it',
  lockedSwitchHelp: 'Keep editing in the other tab. This tab goes back to the Library.',
  lockedHere: 'Edit here instead',
  lockedHereHelp: 'The other tab saves the chart, closes it and goes back to its Library.',
  /** "Switch to it": the other tab was asked to come forward (browsers may not switch on their own). */
  lockedSwitched:
    'Phiedit asked the other tab to come forward. If your browser did not switch, pick the Phiedit tab with "{name}" in the tab bar.',
  /** "Switch to it" where tabs cannot talk to each other. */
  lockedSwitchManual: 'Pick the Phiedit tab with "{name}" in the tab bar to keep editing it there.',
  /** The other tab did not give the chart up. */
  lockedKept: 'The other tab kept "{name}". Switch to that tab to edit it.',
  takeOverFailed: 'The chart could not be moved to this tab: {reason}',
  /** This tab gave the chart to another tab ("Edit here instead" there). */
  handedOff: '"{name}" is now open in another tab. Everything was saved before it moved there.',
  /** Closing the chart failed (its changes stay in the journal and are recovered on the next open). */
  closeFailed:
    'The chart was not closed cleanly ({reason}). Its changes are kept and come back when you open it again.',
  /** The default skin could not be loaded at start-up. */
  skinFailed: 'The skin could not be loaded ({reason}). The built-in skin is used instead.',
  /** An optional part of the editor could not start in this browser. */
  unavailable: '{feature} is not available in this browser: {reason}',
  /** Fallback reason when the browser gave none. */
  unknownReason: 'unknown error',
  /** Reason: the browser has no Web Audio, or refused to start it. */
  reasonNoWebAudio: 'the browser has no Web Audio or does not allow it here, so the preview plays without sound',
  /** Reason: a background worker could not be started (and the browser said nothing more). */
  reasonWorkerNotStarted: 'its background worker could not be started',
  /** Reason: the part was shut down (the app is closing). */
  reasonWorkerStopped: 'it was shut down',
  featureAudio: 'Audio playback',
  featureIo: 'Import and export',
  featureAnalysis: 'The chart checker',
  featureDsp: 'Audio analysis (waveform, tempo detection)',
  featureSkins: 'Skins',
  featureProblems: 'The Problems list',
  featureTextures: 'Chart images',
});

export default catalogOf(m);
